/**
 * Copilot store — conversation, proposed actions, execution timeline and
 * widget chrome state (position / minimized / mode are persisted).
 *
 * Degraded mode: the copilot backend (/api/v1/copilot/*) IS deployed, so a
 * failure is only "not deployed" when the endpoint is genuinely absent
 * (404 / 501 — an old or rolled-back backend). Then `backendAvailable` flips to
 * false, the history stays local, and lightweight simulated actions are proposed
 * with the `preview` flag ("aperçu" badge). A transient failure (429 / 5xx /
 * network) must NOT masquerade as "not deployed / simulated" — that is an
 * invisible failure; it surfaces an honest error and leaves the backend marked
 * available.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { api, ApiError } from '@/lib/api/client';
import { runRegisteredAction } from '@/features/copilot/action-executor';
import { normalizeActions } from '@/features/copilot/normalize';
import type {
  CopilotAction,
  CopilotMessage,
  CopilotMode,
  CopilotTimelineEntry,
  CopilotToast,
  ExecutionStatus,
  PageContext,
} from '@/features/copilot/types';

const FALLBACK_ASSISTANT_CONTENT =
  'Backend copilot non déployé — vos messages sont conservés localement.';

function uid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

function defaultPosition(): { x: number; y: number } {
  if (typeof window === 'undefined') return { x: 24, y: 24 };
  return {
    x: Math.max(16, window.innerWidth - 396),
    y: Math.max(16, window.innerHeight - 616),
  };
}

/**
 * Local heuristic used when the backend is unavailable: propose simulated
 * ("aperçu") actions so the Action Protocol can still be demonstrated.
 */
function buildPreviewActions(content: string, pageContext: PageContext | null): CopilotAction[] {
  const actions: CopilotAction[] = [];

  const routeMatch = content.match(/(?:^|\s)(\/[a-z0-9][a-z0-9\-_/]*)/i);
  if (routeMatch) {
    actions.push({
      action_id: uid(),
      type: 'navigate',
      target: { route: routeMatch[1] },
      requires_confirmation: true,
      preview: true,
    });
  }

  if (pageContext?.formSchema && /rempli|formulaire|champ|fill/i.test(content)) {
    actions.push({
      action_id: uid(),
      type: 'highlight',
      target: { page_id: pageContext.pageId, form_id: pageContext.formSchema.formId },
      requires_confirmation: true,
      preview: true,
    });
  }

  return actions;
}

interface SessionResponse {
  id: string;
}

interface MessageResponse {
  message: { role: 'assistant'; content: string };
  actions?: CopilotAction[];
}

interface CopilotState {
  // Widget chrome (position / minimized / mode are persisted)
  open: boolean;
  minimized: boolean;
  position: { x: number; y: number };
  mode: CopilotMode;

  // Advanced power-user options (persisted). Sent to the backend as `options`;
  // ignored gracefully until the copilot service consumes them.
  model: string;
  temperature: number;
  maxSteps: number;

  // Conversation
  sessionId: string | null;
  messages: CopilotMessage[];
  pendingActions: CopilotAction[];
  timeline: CopilotTimelineEntry[];
  toasts: CopilotToast[];
  backendAvailable: boolean;
  sending: boolean;

  // Actions
  toggleOpen: () => void;
  openWidget: () => void;
  setMinimized: (minimized: boolean) => void;
  setPosition: (position: { x: number; y: number }) => void;
  setMode: (mode: CopilotMode) => void;
  setModel: (model: string) => void;
  setTemperature: (temperature: number) => void;
  setMaxSteps: (maxSteps: number) => void;
  sendMessage: (content: string, pageContext?: PageContext | null) => Promise<void>;
  confirmAction: (actionId: string) => void;
  rejectAction: (actionId: string) => void;
  recordExecution: (action: CopilotAction, status: ExecutionStatus, detail?: string) => void;
  pushToast: (message: string) => void;
  dismissToast: (toastId: string) => void;
}

export const useCopilotStore = create<CopilotState>()(
  persist(
    (set, get) => ({
      open: false,
      minimized: false,
      position: defaultPosition(),
      mode: 'assistant',

      model: 'auto',
      temperature: 0.7,
      maxSteps: 5,

      sessionId: null,
      messages: [],
      pendingActions: [],
      timeline: [],
      toasts: [],
      backendAvailable: true,
      sending: false,

      toggleOpen: () => set((s) => ({ open: !s.open })),

      openWidget: () => set({ open: true, minimized: false }),

      setMinimized: (minimized) => set({ minimized }),

      setPosition: (position) => set({ position }),

      setMode: (mode) => set({ mode }),

      setModel: (model) => set({ model }),

      setTemperature: (temperature) =>
        set({ temperature: Math.min(1, Math.max(0, temperature)) }),

      setMaxSteps: (maxSteps) => set({ maxSteps: Math.min(10, Math.max(1, Math.round(maxSteps))) }),

      sendMessage: async (content, pageContext) => {
        const text = content.trim();
        if (!text || get().sending) return;

        const userMessage: CopilotMessage = {
          id: uid(),
          role: 'user',
          content: text,
          at: nowIso(),
        };
        set((s) => ({ messages: [...s.messages, userMessage], sending: true }));

        const { mode } = get();
        try {
          let sessionId = get().sessionId;
          if (!sessionId) {
            const session = await api.post<SessionResponse>('/api/v1/copilot/sessions', { mode });
            sessionId = session.id;
            set({ sessionId });
          }

          const { model, temperature, maxSteps } = get();
          const response = await api.post<MessageResponse>(
            `/api/v1/copilot/sessions/${sessionId}/messages`,
            {
              content: text,
              mode,
              page_context: pageContext ?? null,
              // Advanced options — the backend ignores unknown fields until it
              // consumes them (Pydantic default), so this never breaks the call.
              options: { model, temperature, max_steps: maxSteps },
            },
          );

          // Normalize the wire shape (backend sends `id`; the widget keys/confirms
          // actions by `action_id`) so multi-card confirm/reject is always 1:1.
          const actions = normalizeActions(response.actions);
          // Agent mode auto-executes actions that do not require confirmation;
          // everything else lands in the pending queue (cards in the widget).
          const autoRun = actions.filter((a) => !a.requires_confirmation && mode === 'agent');
          const pending = actions.filter((a) => a.requires_confirmation || mode !== 'agent');

          const assistantMessage: CopilotMessage = {
            id: uid(),
            role: 'assistant',
            content: response.message.content,
            actions: actions.length > 0 ? actions : undefined,
            at: nowIso(),
          };

          set((s) => ({
            messages: [...s.messages, assistantMessage],
            pendingActions: [...s.pendingActions, ...pending],
            backendAvailable: true,
            sending: false,
          }));

          // BUG D: run auto-executed actions SEQUENTIALLY, not fire-and-forget — a
          // fill_field/submit_form/open_form after a navigate must wait for the
          // destination page to mount (its target elements don't exist yet), and give
          // each navigation a render tick before the next action runs.
          void (async () => {
            for (const action of autoRun) {
              try {
                await runRegisteredAction(action);
              } catch {
                /* runRegisteredAction records its own failure outcome */
              }
              if (action.type === 'navigate') {
                await new Promise((resolve) => setTimeout(resolve, 400));
              }
            }
          })();
        } catch (err) {
          const status = err instanceof ApiError ? err.status : 0;
          const endpointAbsent = status === 404 || status === 501;
          if (endpointAbsent) {
            // Genuine absence (old / rolled-back backend): degrade to local
            // preview mode with simulated "aperçu" actions.
            const preview = buildPreviewActions(text, pageContext ?? null);
            const assistantMessage: CopilotMessage = {
              id: uid(),
              role: 'assistant',
              content: FALLBACK_ASSISTANT_CONTENT,
              actions: preview.length > 0 ? preview : undefined,
              at: nowIso(),
            };
            set((s) => ({
              messages: [...s.messages, assistantMessage],
              pendingActions: [...s.pendingActions, ...preview],
              backendAvailable: false,
              sending: false,
            }));
            return;
          }
          // The backend IS deployed but this turn failed transiently (rate limit /
          // 5xx / network). Tell the truth — do NOT flip to "simulated" mode or
          // fabricate preview actions, which would hide a real, retryable failure.
          const reason =
            status === 429
              ? 'Trop de requêtes — limite temporairement atteinte. Patientez un instant puis réessayez.'
              : status === 0
                ? 'Copilot momentanément injoignable — vérifiez votre connexion et réessayez.'
                : 'Le copilot a rencontré une erreur temporaire. Réessayez dans un instant.';
          const assistantMessage: CopilotMessage = {
            id: uid(),
            role: 'assistant',
            content: reason,
            at: nowIso(),
          };
          set((s) => ({
            messages: [...s.messages, assistantMessage],
            // Backend is deployed — keep it marked available; this was transient.
            sending: false,
          }));
        }
      },

      confirmAction: (actionId) => {
        const action = get().pendingActions.find((a) => a.action_id === actionId);
        if (!action) return;
        set((s) => ({
          pendingActions: s.pendingActions.filter((a) => a.action_id !== actionId),
        }));
        void runRegisteredAction(action);
      },

      rejectAction: (actionId) => {
        const action = get().pendingActions.find((a) => a.action_id === actionId);
        if (!action) return;
        set((s) => ({
          pendingActions: s.pendingActions.filter((a) => a.action_id !== actionId),
        }));
        get().recordExecution(action, 'skipped', 'Rejetée par l’utilisateur');
      },

      recordExecution: (action, status, detail) =>
        set((s) => ({
          timeline: [...s.timeline, { action, status, detail, at: nowIso() }],
        })),

      pushToast: (message) => {
        const toast: CopilotToast = { id: uid(), message, at: nowIso() };
        set((s) => ({ toasts: [...s.toasts, toast] }));
        if (typeof window !== 'undefined') {
          window.setTimeout(() => {
            set((s) => ({ toasts: s.toasts.filter((t) => t.id !== toast.id) }));
          }, 5000);
        }
      },

      dismissToast: (toastId) =>
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== toastId) })),
    }),
    {
      name: 'psn-copilot',
      version: 1,
      partialize: (s) => ({
        position: s.position,
        minimized: s.minimized,
        mode: s.mode,
        model: s.model,
        temperature: s.temperature,
        maxSteps: s.maxSteps,
      }),
    },
  ),
);
